import { FloatingFormItem } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import { useInsurerDataReport } from '@/core/domain/reports/people/Report.hooks';
import { InsurerReportFormValues } from '@/core/domain/reports/people/Report.types';
import { useCitiesByState } from '@/core/domain/zipcode/Cities/Cities.hooks';
import { useStates } from '@/core/domain/zipcode/States/States.hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useReportHandlers } from '@/hooks/useReportHandler';
import generateCsv from '@/utils/functions/generateCsv';
import { Button, Col, Form, Input, Modal, ModalProps, Row, Select } from 'antd';

export default function InsurerReportModal(props: ModalProps) {
  const [form] = Form.useForm<InsurerReportFormValues>();
  const filters = Form.useWatch([], form);
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const { data: states, isLoading: stateLoading } = useStates();
  const { data: cities, isLoading: cityLoading } = useCitiesByState(
    filters?.uf?.length === 1 ? filters.uf.at(0) : undefined,
  );

  const statesOptions = states?.data.map(({ code }) => ({ value: code }));
  const citiesOptions = cities?.data.map(({ name, id }) => ({
    label: name,
    value: id,
  }));
  const { mutateAsync: insurerDataReport } = useInsurerDataReport();
  const generate = useCommonGenerateReport<InsurerReportFormValues>(
    '/generate/insurers',
    'Relatório gerado com sucesso!',
    'Relatório de seguradora gerado com sucesso',
  );

  const handlePrintReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const result = await generate.mutateAsync(filters);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleCsv = async () => {
    const data = await insurerDataReport(filters);
    const csvData = data.map(({ name, address, email, phone }) => ({
      ['Nome']: name,
      ['Endereço']: address,
      ['Email']: email,
      ['Telefone']: phone,
    }));
    generateCsv(csvData, 'Seguradoras');
  };

  const { handleView, handlePdf } = useReportHandlers(
    'Seguradoras',
    handlePrintReport,
  );

  return (
    <Modal
      {...props}
      title="Gerar relatório"
      footer={
        <SpaceForButton justifyContent="end">
          <Button
            type="primary"
            block
            onClick={handleView}
            loading={fileLoading.REPORT_VIEW}
          >
            Visualizar
          </Button>
          <Button
            type="primary"
            block
            onClick={handlePdf}
            loading={fileLoading.PDF}
          >
            Gerar PDF
          </Button>
          <Button type="primary" onClick={handleCsv} block>
            Gerar CSV
          </Button>
        </SpaceForButton>
      }
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col sm={24} md={16}>
            <FloatingFormItem name="name" label="Nome">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col sm={24} md={8}>
            <FloatingFormItem name="active" label="Status">
              <Select
                options={[
                  { label: 'Ativo', value: 1 },
                  { label: 'Inativo', value: 0 },
                ]}
                allowClear
              />
            </FloatingFormItem>
          </Col>

          <Col sm={24} md={12}>
            <FloatingFormItem name="uf" label="Estado">
              <Select
                options={statesOptions}
                loading={stateLoading}
                onChange={() => form.resetFields(['cityIds'])}
                mode="multiple"
                style={{ width: '100%' }}
                maxTagCount={8}
                allowClear
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.value as string)
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              />
            </FloatingFormItem>
          </Col>

          <Col sm={24} md={12}>
            <FloatingFormItem name="cityIds" label="Cidade">
              <Select
                options={citiesOptions}
                loading={cityLoading}
                disabled={filters?.uf?.length !== 1}
                mode="multiple"
                style={{ width: '100%' }}
                maxTagCount={4}
                allowClear
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label as string)
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
